import { Fragment, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { RING_STATUS } from "score-common";
import { useActiveOrg, userDisplayName } from "common-fe";
import { useScoreApi } from "../api/scoreApi";
import useRingStates from "../hooks/useRingStates";
import useOrgMembersById from "../hooks/useOrgMembersById";
import { classNumberLabel } from "../util/classDisplay";
import SetRingStateDialog from "./SetRingStateDialog";

// #1936 (docs/score/ring-state.md) — the Ring section: every ring in the
// show, in #1909's `ringsFor` order, with what's in it, where that value
// came from, and one-click controls to fix it. This is the screen a person
// opens when a board is showing the wrong thing during a live class (the
// card's framing), so every action here is a single click/dialog-submit —
// Clear costs no more steps than Set did (AC5), and Break/End of day are
// one PUT each with no class/ride (docs/score/ring-state.md: "carry
// neither").
//
// Clearing uses DELETE /ring-state/:ring (scoreApi.ringState.clearRingState),
// never a client-reconstructed "derived" guess PUT back as EXPLICIT — an
// absent row already IS the automatic state (AC7), and the next scoring
// write recreates it fresh via mergeRingStateOnScore's `!existing` branch.
function formatSetAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

// #2075 — a ring-state row's `setBy` is a raw Cognito sub; it must never
// reach the screen as-is (bug #182). Resolve through the org's member map
// (useOrgMembersById) to a "First Last"/email display name (common-fe's
// userDisplayName, #736's helper), and fall back to a neutral label — never
// an empty cell, never the sub itself — when the sub isn't a current org
// member (a former user, or a row a service wrote).
function setByLabel(setBy, membersById, unknownLabel) {
  if (!setBy) return null;
  return userDisplayName(membersById[setBy]) || unknownLabel;
}

// Semantic MUI palette colors (success/warning/default), not "primary" —
// primary reads as invisible grey in dark mode (CLAUDE.md UI convention).
function statusChipProps(status) {
  switch (status) {
    case RING_STATUS.RUNNING:
      return { color: "success", variant: "filled" };
    case RING_STATUS.BREAK:
      return { color: "warning", variant: "filled" };
    case RING_STATUS.END_OF_DAY:
      return { color: "default", variant: "outlined" };
    default:
      return { color: "default", variant: "outlined" };
  }
}

// Static i18n keys as whole literal strings so i18next-parser and the
// missing-key audit can see them (CLAUDE.md: no dynamic i18n keys).
const STATUS_LABEL_KEYS = {
  [RING_STATUS.RUNNING]: "ringState.status.RUNNING",
  [RING_STATUS.BREAK]: "ringState.status.BREAK",
  [RING_STATUS.END_OF_DAY]: "ringState.status.END_OF_DAY",
};

export default function RingStateSection({ showId }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const { rows, classes, ridesByClassId, loading, error, refetch } = useRingStates(showId);
  const { activeOrgId } = useActiveOrg();
  const membersById = useOrgMembersById(activeOrgId);

  const [setDialogRing, setSetDialogRing] = useState(null);
  const [busyRing, setBusyRing] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  const clearRowError = (ring) =>
    setRowErrors((prev) => {
      if (!(ring in prev)) return prev;
      const next = { ...prev };
      delete next[ring];
      return next;
    });

  const runAction = async (ring, fn) => {
    setBusyRing(ring);
    clearRowError(ring);
    try {
      await fn();
      await refetch();
    } catch (err) {
      const code = err?.response?.data?.code;
      const message =
        code === "running_requires_class"
          ? t("ringState.errors.runningRequiresClass")
          : err?.serverErrorDetail || err?.message || t("ringState.errors.generic");
      setRowErrors((prev) => ({ ...prev, [ring]: message }));
    } finally {
      setBusyRing(null);
    }
  };

  const handleBreak = (ring) =>
    runAction(ring, () =>
      scoreApi.ringState.setRingState({
        showId,
        ring,
        status: RING_STATUS.BREAK,
        classId: null,
        rideId: null,
      })
    );

  const handleEndOfDay = (ring) =>
    runAction(ring, () =>
      scoreApi.ringState.setRingState({
        showId,
        ring,
        status: RING_STATUS.END_OF_DAY,
        classId: null,
        rideId: null,
      })
    );

  const handleClear = (ring) =>
    runAction(ring, () => scoreApi.ringState.clearRingState(showId, ring));

  if (!showId) return null;

  return (
    <Card variant="outlined" data-testid="ring-state-section">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("ringState.title")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="ring-state-load-error">
            {t("ringState.loadError")}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">{t("ringState.noRings")}</Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" data-testid="ring-state-table">
              <TableHead>
                <TableRow>
                  <TableCell>{t("ringState.columns.ring")}</TableCell>
                  <TableCell>{t("ringState.columns.status")}</TableCell>
                  <TableCell>{t("ringState.columns.class")}</TableCell>
                  <TableCell>{t("ringState.columns.ride")}</TableCell>
                  <TableCell>{t("ringState.columns.source")}</TableCell>
                  <TableCell>{t("ringState.columns.setAt")}</TableCell>
                  <TableCell>{t("ringState.columns.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const busy = busyRing === row.ring;
                  const statusProps = statusChipProps(row.status);
                  const statusLabel = t(
                    STATUS_LABEL_KEYS[row.status] ?? "ringState.status.NONE"
                  );
                  const setAtLabel = formatSetAt(row.setAt);
                  const setByDisplay = setByLabel(row.setBy, membersById, t("common.unknownUser"));

                  return (
                    <Fragment key={row.ring}>
                      <TableRow data-testid={`ring-row-${row.ring}`}>
                        <TableCell>
                          <Typography variant="body2" color="text.primary">
                            {row.ring}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusLabel}
                            data-testid={`ring-status-${row.ring}`}
                            {...statusProps}
                          />
                        </TableCell>
                        <TableCell>
                          {row.class ? (
                            <Stack spacing={0.5}>
                              <Typography variant="body2" color="text.primary">
                                {classNumberLabel(row.class)}
                              </Typography>
                              {row.classRunning != null && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={row.classRunning ? "info" : "default"}
                                  data-testid={`ring-class-state-${row.ring}`}
                                  label={
                                    row.classRunning
                                      ? t("ringState.classState.running")
                                      : t("ringState.classState.finished")
                                  }
                                />
                              )}
                            </Stack>
                          ) : (
                            <Typography color="text.secondary">{t("ringState.noClass")}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.ride ? (
                            <Typography variant="body2" color="text.primary">
                              {row.ride.riderName || row.ride.horseName || t("common.unknownUser")}
                            </Typography>
                          ) : (
                            <Typography color="text.secondary">{t("ringState.noRide")}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.hasOverride ? (
                            <Chip
                              size="small"
                              color={row.isExplicit ? "warning" : "default"}
                              variant={row.isExplicit ? "filled" : "outlined"}
                              label={
                                row.isExplicit
                                  ? t("ringState.source.explicit")
                                  : t("ringState.source.automatic")
                              }
                              data-testid={`ring-source-${row.ring}`}
                            />
                          ) : (
                            <Typography color="text.secondary" data-testid={`ring-source-${row.ring}`}>
                              {t("ringState.source.none")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.primary">
                            {setAtLabel || "-"}
                          </Typography>
                          {setByDisplay && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              data-testid={`ring-set-by-${row.ring}`}
                            >
                              {setByDisplay}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {/* #2076/bug #185 — flexWrap="wrap" let CLEAR (only
                              rendered when row.hasOverride) drop to its own
                              line under SET/BREAK/END OF DAY, making that
                              row taller than rings without an override and
                              breaking the column's vertical alignment down
                              the page. Keep the Actions row on one line;
                              TableContainer's overflowX:auto (above) scrolls
                              the whole table horizontally if it's ever
                              narrower than four buttons need. */}
                          <Stack direction="row" spacing={1} flexWrap="nowrap">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              data-testid={`ring-action-set-${row.ring}`}
                              onClick={() => setSetDialogRing(row.ring)}
                            >
                              {t("ringState.actions.set")}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              data-testid={`ring-action-break-${row.ring}`}
                              onClick={() => handleBreak(row.ring)}
                            >
                              {t("ringState.actions.break")}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              data-testid={`ring-action-endofday-${row.ring}`}
                              onClick={() => handleEndOfDay(row.ring)}
                            >
                              {t("ringState.actions.endOfDay")}
                            </Button>
                            {row.hasOverride && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={busy}
                                data-testid={`ring-action-clear-${row.ring}`}
                                onClick={() => handleClear(row.ring)}
                              >
                                {busy ? <CircularProgress size={16} /> : t("ringState.actions.clear")}
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {rowErrors[row.ring] && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ pt: 0, pb: 1, border: 0 }}>
                            <Alert severity="error" data-testid={`ring-error-${row.ring}`}>
                              {rowErrors[row.ring]}
                            </Alert>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <SetRingStateDialog
        open={!!setDialogRing}
        ring={setDialogRing}
        showId={showId}
        classes={classes}
        ridesByClassId={ridesByClassId}
        onClose={() => setSetDialogRing(null)}
        onSaved={() => {
          setSetDialogRing(null);
          refetch();
        }}
      />
    </Card>
  );
}
